/** Kopfzeile: Titel, Datei-Aktionen, Undo/Redo, Ansichten, Hilfe. */
import type { CollabStatus } from '../collab/collabClient';

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/**
 * 'project' = an ein Server-Projekt gebunden (Zurück, Name, Speicherstatus)
 * 'draft'   = anonymer Entwurf (Speichern erzeugt erst ein Konto)
 */
export type TopBarMode = 'project' | 'draft';

export interface TopBarActions {
  mode: TopBarMode;
  onNew(): void;
  /** project: Server-Speicherung erzwingen · draft: Konto-Gate + Projekt anlegen */
  onSave(): void;
  /** .campsketch.json herunterladen (beide Modi). */
  onExport(): void;
  /** .campsketch.json öffnen (project: neues Server-Projekt · draft: Entwurf ersetzen). */
  onImport(): void;
  /** Nur project: zurück zur Projektübersicht. */
  onBack?(): void;
  /** Nur project: Projekt über das Namensfeld umbenennen. */
  onRenameProject?(name: string): void;
  onUndo(): void;
  onRedo(): void;
  onGroup(): void;
  onUngroup(): void;
  /** Spiegeln entlang rot (0), grün (1), blau (2). */
  onFlip(axis: 0 | 1 | 2): void;
  onView(view: string): void;
  onZoomExtents(): void;
  onHelp(): void;
  onShare(): void;
  /** Folgen-Modus beenden (Chip-Klick). */
  onStopFollow(): void;
  /** Rechte Seitenleiste (Panels) ein-/ausblenden. */
  onTogglePanels(): void;
}

export class TopBar {
  el: HTMLElement;
  /** Haltepunkt für die Teilnehmer-Leiste (Avatare, Ein-Tipp-Folgen). */
  readonly participantsRoot: HTMLDivElement;
  private undoBtn: HTMLButtonElement;
  private redoBtn: HTMLButtonElement;
  private collabChip: HTMLButtonElement;
  private collabDot: HTMLSpanElement;
  private collabCount: HTMLSpanElement;
  private followChip: HTMLButtonElement;
  private panelsToggle: HTMLButtonElement;
  private nameInput: HTMLInputElement | null = null;
  private saveStateEl: HTMLSpanElement | null = null;

  constructor(root: HTMLElement, a: TopBarActions) {
    this.el = document.createElement('div');
    this.el.className = 'topbar';
    this.participantsRoot = document.createElement('div');
    this.participantsRoot.className = 'participants-root';

    const logo = document.createElement('div');
    logo.className = 'logo';
    logo.innerHTML = '<svg viewBox="0 0 100 100" width="22" height="22"><rect x="8" y="30" width="70" height="45" rx="8" fill="#e0552c"/><circle cx="28" cy="78" r="9" fill="#222"/><circle cx="68" cy="78" r="9" fill="#222"/></svg><span>CampSketch</span>';

    const mkBtn = (label: string, title: string, fn: () => void, className = 'top-btn'): HTMLButtonElement => {
      const b = document.createElement('button');
      b.className = className;
      b.textContent = label;
      b.title = title;
      b.onclick = fn;
      return b;
    };

    this.undoBtn = mkBtn('↶', 'Rückgängig (Strg+Z)', a.onUndo);
    this.redoBtn = mkBtn('↷', 'Wiederholen (Strg+Umschalt+Z)', a.onRedo);

    this.collabChip = document.createElement('button');
    this.collabChip.className = 'top-btn collab-chip';
    this.collabChip.title = 'Kollaboration — Teilnehmer anzeigen';
    this.collabChip.hidden = true;
    this.collabChip.onclick = a.onShare;
    this.collabDot = document.createElement('span');
    this.collabDot.className = 'collab-dot';
    this.collabCount = document.createElement('span');
    this.collabChip.append(this.collabDot, this.collabCount);

    // Folgen-Modus: sichtbar, solange die Ansicht eines anderen gespiegelt wird
    this.followChip = document.createElement('button');
    this.followChip.className = 'top-btn follow-chip';
    this.followChip.type = 'button';
    this.followChip.hidden = true;
    this.followChip.onclick = a.onStopFollow;

    // Seitenleisten-Umschalter — mobil ist die Leiste standardmäßig eingeklappt
    this.panelsToggle = document.createElement('button');
    this.panelsToggle.className = 'top-btn panels-toggle';
    this.panelsToggle.type = 'button';
    this.panelsToggle.textContent = '☰';
    this.panelsToggle.onclick = a.onTogglePanels;

    const views = document.createElement('select');
    views.className = 'view-select';
    views.title = 'Standardansicht';
    views.innerHTML = `
      <option value="">Ansicht…</option>
      <option value="iso">Isometrie</option>
      <option value="top">Von oben</option>
      <option value="front">Von vorn</option>
      <option value="back">Von hinten</option>
      <option value="left">Von links</option>
      <option value="right">Von rechts</option>`;
    views.onchange = () => {
      if (views.value) {
        a.onView(views.value);
        views.value = '';
      }
    };

    if (a.mode === 'project') {
      // Projekt-Modus: Projektliste, Name, Speicherstatus, Export/Import
      const backBtn = mkBtn('← Projekte', 'Zur Projektübersicht', () => a.onBack?.());
      const nameInput = document.createElement('input');
      nameInput.className = 'project-name-input';
      nameInput.type = 'text';
      nameInput.maxLength = 80;
      nameInput.placeholder = 'Unbenanntes Projekt';
      nameInput.title = 'Projektname — ändern und Enter drücken';
      const commitName = (): void => {
        const name = nameInput.value.trim();
        if (name && name !== nameInput.defaultValue) a.onRenameProject?.(name);
      };
      // Enter benennt direkt um (change beim Verlassen bleibt als Fallback)
      nameInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commitName();
          nameInput.blur();
        }
      });
      nameInput.onchange = commitName;
      this.nameInput = nameInput;

      this.saveStateEl = document.createElement('span');
      this.saveStateEl.className = 'save-state';
      this.saveStateEl.textContent = '';

      this.el.append(
        logo,
        this.panelsToggle,
        backBtn,
        nameInput,
        this.saveStateEl,
        mkBtn('Neu', 'Neues Projekt anlegen', a.onNew),
        mkBtn('Export', 'Als .campsketch.json exportieren', a.onExport),
        mkBtn('Import', '.campsketch.json als neues Projekt importieren', a.onImport),
        mkBtn('Teilen', 'Zusammen zeichnen — Link verschicken', a.onShare),
        this.collabChip,
        this.followChip,
        this.participantsRoot,
        this.undoBtn,
        this.redoBtn,
        mkBtn('Gruppieren', 'Auswahl gruppieren (Strg+G)', a.onGroup),
        mkBtn('Lösen', 'Gruppe auflösen (Strg+Umschalt+G)', a.onUngroup),
        mkBtn('⇆', 'Horizontal spiegeln – rote Achse (Umschalt+H)', () => a.onFlip(0)),
        mkBtn('⇄', 'Horizontal spiegeln – grüne Achse (Umschalt+J)', () => a.onFlip(1)),
        mkBtn('⇅', 'Vertikal spiegeln – blaue Achse (Umschalt+V)', () => a.onFlip(2)),
        views,
        mkBtn('⤢', 'Auf alles zoomen (Strg+Umschalt+E)', a.onZoomExtents),
        mkBtn('?', 'Tastenkürzel anzeigen', a.onHelp)
      );
    } else {
      // Entwurf-Modus: anonym zeichnen — „Speichern“ legt erst das Konto an
      this.el.append(
        logo,
        this.panelsToggle,
        mkBtn('Neu', 'Neue Zeichnung anlegen (lokaler Entwurf)', a.onNew),
        mkBtn('Speichern', 'Als Projekt speichern — kostenloses Konto erstellen (3 Projekte gratis)', a.onSave),
        mkBtn('Export', 'Als .campsketch.json herunterladen', a.onExport),
        mkBtn('Import', '.campsketch.json öffnen', a.onImport),
        mkBtn('Teilen', 'Zusammen zeichnen — Link verschicken', a.onShare),
        this.collabChip,
        this.followChip,
        this.participantsRoot,
        this.undoBtn,
        this.redoBtn,
        mkBtn('Gruppieren', 'Auswahl gruppieren (Strg+G)', a.onGroup),
        mkBtn('Lösen', 'Gruppe auflösen (Strg+Umschalt+G)', a.onUngroup),
        mkBtn('⇆', 'Horizontal spiegeln – rote Achse (Umschalt+H)', () => a.onFlip(0)),
        mkBtn('⇄', 'Horizontal spiegeln – grüne Achse (Umschalt+J)', () => a.onFlip(1)),
        mkBtn('⇅', 'Vertikal spiegeln – blaue Achse (Umschalt+V)', () => a.onFlip(2)),
        views,
        mkBtn('⤢', 'Auf alles zoomen (Strg+Umschalt+E)', a.onZoomExtents),
        mkBtn('?', 'Tastenkürzel anzeigen', a.onHelp)
      );
    }
    root.appendChild(this.el);
  }

  setUndoRedo(canUndo: boolean, canRedo: boolean): void {
    this.undoBtn.disabled = !canUndo;
    this.redoBtn.disabled = !canRedo;
  }

  setProjectName(name: string): void {
    if (!this.nameInput) return;
    this.nameInput.value = name;
    this.nameInput.defaultValue = name;
  }

  /** Speicherstatus der Server-Speicherung (nur Projekt-Modus sichtbar). */
  setSaveState(state: SaveState): void {
    if (!this.saveStateEl) return;
    this.saveStateEl.dataset.state = state;
    this.saveStateEl.textContent =
      state === 'saving' ? 'Speichert …'
      : state === 'saved' ? 'Gespeichert'
      : state === 'error' ? 'Speichern fehlgeschlagen'
      : '';
  }

  /**
   * Statuschip der Kollaboration. Bleibt sichtbar, solange eine Session existiert —
   * auch bei Verbindungsabbruch (Warnfarbe), damit man den Reconnect mitbekommt.
   */
  setCollabStatus(status: CollabStatus, peerCount: number, sessionActive = true): void {
    if (!sessionActive && status === 'offline') {
      this.collabChip.hidden = true;
      return;
    }
    this.collabChip.hidden = false;
    this.collabChip.dataset.status = status;
    this.collabChip.title =
      status === 'online' ? `Kollaboration aktiv — ${peerCount} Teilnehmer`
      : status === 'connecting' ? 'Verbinde mit dem Raum …'
      : status === 'offline' ? 'Kollaboration getrennt — verbinde erneut …'
      : 'Kollaboration fehlgeschlagen';
    this.collabCount.textContent = status === 'online' ? String(peerCount) : '!';
  }

  /** Folgen-Modus anzeigen/beenden: zeigt, wessen Ansicht gerade gespiegelt wird. */
  setFollowing(name: string | null): void {
    this.followChip.hidden = !name;
    if (!name) return;
    this.followChip.textContent = `Folge: ${name} ✕`;
    this.followChip.title = `Du siehst, was „${name}“ sieht — klicken beendet das Folgen`;
  }

  /** Zustand des Seitenleisten-Umschalters spiegeln (Titel + Aktiv-Markierung). */
  setPanelsHidden(hidden: boolean): void {
    this.panelsToggle.title = hidden ? 'Seitenleiste einblenden (Van, Möbel, Ebenen)' : 'Seitenleiste ausblenden';
    this.panelsToggle.dataset.open = hidden ? 'false' : 'true';
  }
}
